export type TripImportProgressStageId =
  | 'reading'
  | 'finding_places'
  | 'redacting'
  | 'matching'
  | 'organizing';

export type TripImportProgressStageStatus = 'pending' | 'active' | 'done';

export interface TripImportProgressStage {
  id: TripImportProgressStageId;
  label: string;
  status: TripImportProgressStageStatus;
}

export interface TripImportProgressState {
  stages: TripImportProgressStage[];
  progressPercent: number;
}

export interface TripImportFoundPlacePreview {
  client_id: string;
  name: string;
  row_kind?: 'place' | 'travel';
  category?: string | null;
  match_confidence?: 'high' | 'low' | 'none';
  subtitle?: string | null;
  is_bookable?: boolean;
}

export type TripImportProgressCopy = 'document' | 'social';

type StageDef = { id: TripImportProgressStageId; label: string; advanceAfterMs: number };

const DOCUMENT_STAGE_DEFS: StageDef[] = [
  { id: 'reading', label: 'Reading document', advanceAfterMs: 2500 },
  { id: 'finding_places', label: 'Finding places', advanceAfterMs: 28000 },
  { id: 'redacting', label: 'Removing personal information', advanceAfterMs: 3500 },
  {
    id: 'matching',
    label: 'Matching hotels & experiences to booking links',
    advanceAfterMs: 12000,
  },
  { id: 'organizing', label: 'Organizing into days', advanceAfterMs: 4000 },
];

const SOCIAL_STAGE_DEFS: StageDef[] = [
  { id: 'reading', label: 'Reading post', advanceAfterMs: 2500 },
  { id: 'finding_places', label: 'Finding places', advanceAfterMs: 28000 },
  { id: 'redacting', label: 'Removing personal information', advanceAfterMs: 3500 },
  {
    id: 'matching',
    label: 'Matching to booking links',
    advanceAfterMs: 12000,
  },
  { id: 'organizing', label: 'Organizing into days', advanceAfterMs: 4000 },
];

function stageDefsFor(copy: TripImportProgressCopy): StageDef[] {
  return copy === 'social' ? SOCIAL_STAGE_DEFS : DOCUMENT_STAGE_DEFS;
}

/** Finish animation after import returns (no SSE) — each bullet gets time as "working" then "done". */
const COMPLETION_ACTIVE_DWELL_MS = 1200;
const COMPLETION_BETWEEN_STEPS_MS = 450;
const COMPLETION_FINAL_PAUSE_MS = 750;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

function buildStages(activeIndex: number, copy: TripImportProgressCopy): TripImportProgressStage[] {
  return stageDefsFor(copy).map((def, index) => {
    let status: TripImportProgressStageStatus = 'pending';
    if (index < activeIndex) status = 'done';
    else if (index === activeIndex) status = 'active';
    return { id: def.id, label: def.label, status };
  });
}

function progressPercentForElapsed(elapsedMs: number, copy: TripImportProgressCopy): number {
  const defs = stageDefsFor(copy);
  let cumulative = 0;
  for (let i = 0; i < defs.length; i += 1) {
    const segmentMs = defs[i].advanceAfterMs;
    const segmentEnd = cumulative + segmentMs;
    if (elapsedMs < segmentEnd) {
      const segmentStart = cumulative;
      const within = Math.max(0, elapsedMs - segmentStart);
      const segmentRatio = segmentMs > 0 ? within / segmentMs : 1;
      const overall = (i + segmentRatio) / defs.length;
      return Math.min(92, Math.round(overall * 92));
    }
    cumulative = segmentEnd;
  }
  return 92;
}

function activeIndexForElapsed(elapsedMs: number, copy: TripImportProgressCopy): number {
  const defs = stageDefsFor(copy);
  let cumulative = 0;
  for (let i = 0; i < defs.length; i += 1) {
    cumulative += defs[i].advanceAfterMs;
    if (elapsedMs < cumulative) {
      return i;
    }
  }
  return defs.length - 1;
}

function mergeMonotonicProgress(
  previous: TripImportProgressState,
  next: TripImportProgressState,
  copy: TripImportProgressCopy,
): TripImportProgressState {
  const prevActiveIndex = previous.stages.findIndex((s) => s.status === 'active');
  const nextActiveIndex = next.stages.findIndex((s) => s.status === 'active');
  const prevDoneCount = previous.stages.filter((s) => s.status === 'done').length;
  const nextDoneCount = next.stages.filter((s) => s.status === 'done').length;

  const activeIndex = Math.max(
    prevActiveIndex >= 0 ? prevActiveIndex : 0,
    nextActiveIndex >= 0 ? nextActiveIndex : 0,
    nextDoneCount,
    prevDoneCount,
  );
  const defs = stageDefsFor(copy);
  const clampedActive = Math.min(activeIndex, defs.length - 1);

  const progressPercent = Math.max(previous.progressPercent, next.progressPercent);

  return {
    stages: buildStages(clampedActive, copy),
    progressPercent,
  };
}

export function initialTripImportProgressState(
  copy: TripImportProgressCopy = 'document',
): TripImportProgressState {
  return { stages: buildStages(0, copy), progressPercent: 0 };
}

export function completeTripImportProgressState(
  copy: TripImportProgressCopy = 'document',
): TripImportProgressState {
  return {
    stages: stageDefsFor(copy).map((def) => ({
      id: def.id,
      label: def.label,
      status: 'done',
    })),
    progressPercent: 100,
  };
}

/** Timer-driven checklist while the blocking import request runs. Never regresses vs previous tick. */
export function tripImportProgressReducer(
  previous: TripImportProgressState,
  elapsedMs: number,
  copy: TripImportProgressCopy = 'document',
): TripImportProgressState {
  const activeIndex = activeIndexForElapsed(elapsedMs, copy);
  const next: TripImportProgressState = {
    stages: buildStages(activeIndex, copy),
    progressPercent: progressPercentForElapsed(elapsedMs, copy),
  };
  return mergeMonotonicProgress(previous, next, copy);
}

function completionStages(activeIndex: number, copy: TripImportProgressCopy): TripImportProgressStage[] {
  return stageDefsFor(copy).map((def, idx) => {
    let status: TripImportProgressStageStatus = 'pending';
    if (idx < activeIndex) status = 'done';
    else if (idx === activeIndex) status = 'active';
    return { id: def.id, label: def.label, status };
  });
}

function completionProgressPercent(
  activeIndex: number,
  phase: 'working' | 'done',
  copy: TripImportProgressCopy,
): number {
  const defs = stageDefsFor(copy);
  const step = activeIndex + (phase === 'done' ? 1 : 0.35);
  return Math.min(
    phase === 'done' && activeIndex >= defs.length - 1 ? 100 : 98,
    Math.round((step / defs.length) * 100),
  );
}

/**
 * After import JSON returns, walk remaining checklist steps so the UI does not jump straight to review.
 */
export async function animateTripImportProgressToComplete(
  update: (state: TripImportProgressState) => void,
  from: TripImportProgressState,
  copy: TripImportProgressCopy = 'document',
): Promise<void> {
  const defs = stageDefsFor(copy);
  let doneThrough = from.stages.filter((s) => s.status === 'done').length - 1;
  const activeIdx = from.stages.findIndex((s) => s.status === 'active');
  const firstStep = doneThrough + 1;
  const startStep =
    firstStep <= activeIdx && activeIdx >= 0 ? activeIdx : Math.max(0, firstStep);

  if (startStep >= defs.length) {
    update(completeTripImportProgressState(copy));
    await sleep(COMPLETION_FINAL_PAUSE_MS);
    return;
  }

  for (let step = startStep; step < defs.length; step += 1) {
    update({
      stages: completionStages(step, copy),
      progressPercent: completionProgressPercent(step, 'working', copy),
    });
    await sleep(COMPLETION_ACTIVE_DWELL_MS);

    const isLast = step >= defs.length - 1;
    update({
      stages: defs.map((def, idx) => ({
        id: def.id,
        label: def.label,
        status: (idx <= step ? 'done' : 'pending') as TripImportProgressStageStatus,
      })),
      progressPercent: completionProgressPercent(step, 'done', copy),
    });
    await sleep(isLast ? COMPLETION_FINAL_PAUSE_MS : COMPLETION_BETWEEN_STEPS_MS);
  }

  update(completeTripImportProgressState(copy));
}

function categoryLabelForPreview(
  category: string | null | undefined,
  rowKind: 'place' | 'travel' | undefined,
): string {
  if (rowKind === 'travel') return 'Travel';
  switch (category) {
    case 'stay':
      return 'Hotel';
    case 'eat':
      return 'Restaurant';
    case 'do':
      return 'Activity';
    case 'flight':
      return 'Flight';
    default:
      return 'Place';
  }
}

function subtitleForPreview(place: {
  category?: string | null;
  row_kind?: 'place' | 'travel';
  location?: { address?: string; name?: string; gt_category?: string | null } | null;
}): string {
  const label = categoryLabelForPreview(
    place.category ?? place.location?.gt_category ?? null,
    place.row_kind,
  );
  const where =
    place.location?.address?.split(',')[0]?.trim() ||
    place.location?.name?.trim() ||
    '';
  if (label && where) return `${label} · ${where}`;
  return label || where || '';
}

export function flattenDraftPlacePreviews(
  days: {
    places: {
      client_id: string;
      name: string;
      row_kind?: 'place' | 'travel';
      category?: string | null;
      match_confidence?: 'high' | 'low' | 'none';
      location?: { google_place_id?: string; address?: string; name?: string; gt_category?: string | null } | null;
    }[];
  }[],
): TripImportFoundPlacePreview[] {
  return days.flatMap((day) =>
    day.places.map((place) => ({
      client_id: place.client_id,
      name: place.name,
      row_kind: place.row_kind,
      category: place.category ?? place.location?.gt_category ?? null,
      match_confidence: place.match_confidence,
      subtitle: subtitleForPreview(place),
      is_bookable:
        place.row_kind !== 'travel' &&
        place.match_confidence === 'high' &&
        Boolean(place.location?.google_place_id),
    })),
  );
}
